import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ToastContainer } from 'react-toastify'
import * as Sentry from '@sentry/react'
import 'react-toastify/dist/ReactToastify.css'
import './sentry.ts'
import './index.css'
import './lib/i18n'

import { SessionProvider } from './context/SessionContext'
import { OnboardingProvider } from './context/OnboardingContext'
import { APIQueryError, APIMutationError } from './util/errors'

import { router } from './routes.tsx'

// notify sentry of errors that occur in the queries and mutations
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      const queryError = new APIQueryError(error.message, { cause: error })
      Sentry.captureException(queryError)
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error.name === 'RegistrationClosedError') {
        Sentry.logger.error(error.message)
      } else {
        const mutationError = new APIMutationError(error.message, { cause: error })
        Sentry.captureException(mutationError)
      }
    },
  }),
})

createRoot(document.getElementById('root')!, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Error caught in ErrorBoundary', error, errorInfo.componentStack)
  }),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Recoverable error', error, errorInfo.componentStack)
  }),
}).render(
  <StrictMode>
    <SessionProvider>
      <OnboardingProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ToastContainer
            position='top-right'
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
          <ReactQueryDevtools />
        </QueryClientProvider>
      </OnboardingProvider>
    </SessionProvider>
  </StrictMode>,
)
